import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo, useState, type DragEvent } from 'react';
import { useNavigate } from 'react-router-dom';

import {
  CLIP_TRANSITIONS,
  createHighlight,
  getClip,
  listClips,
  type Clip,
  type ClipChain,
  type ClipTransition,
  type Highlight,
} from '../api/clips';
import { ApiError } from '../api/client';
import { ClipPlayer } from '../components/ClipPlayer';
import { isClipGoneError, useStagedClips } from '../hooks/useStagedClips';
import { eventGameClock, eventLabel, formatClipClock } from '../lib/clipLabels';
import { useHighlightDraft } from '../stores/highlightDraft';
import { listMatches } from '../api/portal';

type SortKey = 'newest' | 'rating' | 'minute';

const CLIP_DND = 'application/x-origo-clip';

function readClipDrag(e: DragEvent): string | null {
  return e.dataTransfer.getData(CLIP_DND) || e.dataTransfer.getData('text/plain') || null;
}

export function ClipsPage() {
  const [matchId, setMatchId] = useState('');
  const [personId, setPersonId] = useState('');
  const [teamId, setTeamId] = useState('');
  const [minRating, setMinRating] = useState<number | ''>('');
  const [eventType, setEventType] = useState('');
  const [tag, setTag] = useState('');
  const [search, setSearch] = useState('');
  const [sort, setSort] = useState<SortKey>('newest');
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [composeOpen, setComposeOpen] = useState(false);
  const [reelIndex, setReelIndex] = useState<number | null>(null);

  const matchesQuery = useQuery({ queryKey: ['portal', 'matches'], queryFn: listMatches });
  const matchLabel = useMemo(() => {
    const map = new Map<string, string>();
    for (const m of matchesQuery.data ?? []) map.set(m.id, `${m.home} - ${m.away}`);
    return (id: string) => map.get(id) ?? '';
  }, [matchesQuery.data]);

  const clipsQuery = useQuery({
    queryKey: ['clips'],
    queryFn: () => listClips({ limit: 200 }),
    retry: false,
    refetchInterval: (q) =>
      (q.state.data ?? []).some((c) => clipIsUpdating(c)) ? 1_200 : 4_000,
  });

  const rawClips = clipsQuery.data ?? [];
  const persons = useMemo(() => uniqueByID(rawClips.flatMap((c) => {
    const out: Array<{ id: string; label: string }> = [];
    if (c.person_id) out.push({ id: c.person_id, label: c.person_name ?? '?' });
    if (c.chain?.person_id) out.push({ id: c.chain.person_id, label: c.chain.person_name ?? '?' });
    return out;
  }), (r) => r), [rawClips]);
  const teams = useMemo(() => uniqueByID(rawClips.flatMap((c) => {
    const out: Array<{ id: string; label: string }> = [];
    if (c.team_id) out.push({ id: c.team_id, label: c.team_name ?? '?' });
    if (c.chain?.team_id) out.push({ id: c.chain.team_id, label: c.chain.team_name ?? '?' });
    return out;
  }), (r) => r), [rawClips]);
  const eventTypes = useMemo(
    () => Array.from(new Set(rawClips.flatMap((c) => [c.event_type, c.chain?.event_type].filter(Boolean) as string[]))).sort(),
    [rawClips],
  );
  const matchOptions = useMemo(() => {
    const fromClips = uniqueByID(rawClips.map((c) => ({
      id: c.match_id,
      label: matchLabel(c.match_id) || c.match_id,
    })), (r) => r);
    const seen = new Set(fromClips.map((m) => m.id));
    const extra = (matchesQuery.data ?? [])
      .filter((m) => !seen.has(m.id))
      .map((m) => ({ id: m.id, label: `${m.home} - ${m.away}` }));
    return [...fromClips, ...extra];
  }, [rawClips, matchesQuery.data, matchLabel]);

  const clips = useMemo(() => {
    const q = search.trim().toLowerCase();
    const filtered = rawClips.filter((c) => {
      if (matchId && c.match_id !== matchId) return false;
      if (personId && c.person_id !== personId && c.chain?.person_id !== personId) return false;
      if (teamId && c.team_id !== teamId && c.chain?.team_id !== teamId) return false;
      if (eventType && c.event_type !== eventType && c.chain?.event_type !== eventType) return false;
      if (minRating !== '' && (c.rating ?? 0) < Number(minRating)) return false;
      if (tag && !(c.tags ?? []).some((t) => t.toLowerCase().includes(tag.toLowerCase()))) return false;
      if (!q) return true;
      const hay = [
        c.person_name, c.team_name, eventLabel(c.event_type), c.event_type,
        c.chain?.person_name, c.chain?.team_name, c.chain ? eventLabel(c.chain.event_type) : '',
        c.chain?.event_type, matchLabel(c.match_id), ...(c.tags ?? []),
      ].filter(Boolean).join(' ').toLowerCase();
      return hay.includes(q);
    });
    const next = [...filtered];
    next.sort((a, b) => {
      if (sort === 'rating') return (b.rating ?? 0) - (a.rating ?? 0);
      if (sort === 'minute') return (a.minute ?? 999) - (b.minute ?? 999);
      return new Date(b.occurred_at).getTime() - new Date(a.occurred_at).getTime();
    });
    return next;
  }, [rawClips, matchId, personId, teamId, eventType, minRating, tag, search, sort, matchLabel]);

  const filtersOn = !!(matchId || personId || teamId || eventType || tag || search || minRating !== '');

  const qc = useQueryClient();
  const selectedQuery = useQuery({
    queryKey: ['clip', selectedId],
    queryFn: async () => {
      try {
        return await getClip(selectedId!);
      } catch (err) {
        if (isClipGoneError(err)) return null;
        throw err;
      }
    },
    enabled: !!selectedId,
    retry: (count, err) => !isClipGoneError(err) && count < 2,
    refetchInterval: (q) => (clipIsUpdating(q.state.data) ? 1_200 : 4_000),
  });

  const selectedGone = !!selectedId && selectedQuery.isFetched && selectedQuery.data === null && !selectedQuery.isError;

  useEffect(() => {
    const clip = selectedQuery.data;
    if (!clip) return;
    qc.setQueriesData({ queryKey: ['clips'] }, (old: unknown) => mergeClipIntoQueryData(old, clip));
  }, [qc, selectedQuery.data]);

  const draft = useHighlightDraft();
  const removeDraft = useHighlightDraft((s) => s.remove);
  useStagedClips();
  const selected = selectedGone ? undefined : (selectedQuery.data ?? undefined);
  const selectedFromList = selectedId
    ? (clipsQuery.data ?? []).find((c) => c.id === selectedId)
    : undefined;
  const selectedBlocked = clipPreviewBlocked(selected) || clipPreviewBlocked(selectedFromList);
  const staged = selected ? draft.clipIds.includes(selected.id) : false;
  const selectedPos = clips.findIndex((c) => c.id === selectedId);

  const clearFilters = () => {
    setMatchId('');
    setPersonId('');
    setTeamId('');
    setEventType('');
    setTag('');
    setSearch('');
    setMinRating('');
  };

  const selectClip = (id: string | null) => {
    setSelectedId(id);
    if (id && reelIndex != null) {
      const i = draft.clipIds.indexOf(id);
      setReelIndex(i >= 0 ? i : null);
    }
  };

  const playReelFrom = (from = 0) => {
    const id = draft.clipIds[from];
    if (!id) return;
    setReelIndex(from);
    setSelectedId(id);
  };

  const stopReel = () => setReelIndex(null);

  const onPreviewEnded = () => {
    if (reelIndex == null) return;
    for (let i = reelIndex + 1; i < draft.clipIds.length; i++) {
      const id = draft.clipIds[i];
      const row = clips.find((c) => c.id === id);
      if (row && clipIsPlayable(row)) {
        setReelIndex(i);
        setSelectedId(id);
        return;
      }
    }
    setReelIndex(null);
  };

  useEffect(() => {
    if (selectedId || clips.length === 0) return;
    setSelectedId(nextPlayableId(clips, null));
  }, [clips, selectedId]);

  useEffect(() => {
    if (!selectedId) return;
    document.querySelector(`[data-clip-row="${selectedId}"]`)?.scrollIntoView({
      block: 'nearest',
    });
  }, [selectedId]);

  useEffect(() => {
    if (!selectedId) return;
    if (!selectedGone && clipsQuery.isLoading && clips.length === 0) return;
    const row = clips.find((c) => c.id === selectedId);
    const stillGood = !!row && (clipIsPlayable(row) || (!selectedGone && clipIsPlayable(selected)));
    if (stillGood) return;

    if (selectedGone) {
      qc.setQueriesData({ queryKey: ['clips'] }, (old: unknown) => dropClipFromQueryData(old, selectedId));
      qc.removeQueries({ queryKey: ['clip', selectedId] });
      removeDraft(selectedId);
    }

    const pool = selectedGone ? clips.filter((c) => c.id !== selectedId) : clips;
    const next = nextPlayableId(pool, selectedId);
    if (next) {
      setSelectedId(next);
      setReelIndex(null);
      return;
    }
    setSelectedId(null);
    setReelIndex(null);
  }, [clips, selectedId, selected, selectedGone, clipsQuery.isLoading, qc, removeDraft]);

  useEffect(() => {
    if (reelIndex != null && reelIndex >= draft.clipIds.length) setReelIndex(null);
  }, [reelIndex, draft.clipIds.length]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const t = e.target as HTMLElement | null;
      if (t && (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA' || t.tagName === 'SELECT' || t.isContentEditable)) return;
      if (composeOpen) return;
      if (e.key === '/' ) {
        e.preventDefault();
        document.getElementById('clip-search')?.focus();
        return;
      }
      if (e.key === 'Escape' && reelIndex != null) {
        stopReel();
        return;
      }
      if ((e.key === 'ArrowDown' || e.key === 'ArrowRight' || e.key === 'j') && selectedPos >= 0 && selectedPos < clips.length - 1) {
        e.preventDefault();
        selectClip(clips[selectedPos + 1].id);
      }
      if ((e.key === 'ArrowUp' || e.key === 'ArrowLeft' || e.key === 'k') && selectedPos > 0) {
        e.preventDefault();
        selectClip(clips[selectedPos - 1].id);
      }
      if ((e.key === 'a' || e.key === 'A') && selectedId) {
        e.preventDefault();
        draft.toggle(selectedId);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [clips, selectedPos, selectedId, composeOpen, reelIndex, draft]);

  const clipsDisabled = clipsQuery.error instanceof ApiError
    && clipsQuery.error.status === 402
    && clipsQuery.error.code === 'clips_disabled';
  if (clipsDisabled) return <UpsellCard />;

  return (
    <div className="flex h-full min-h-0 flex-col">
      <div className="flex min-h-0 flex-1 flex-col md:flex-row">
        <aside className="flex max-h-[42vh] w-full shrink-0 flex-col border-b border-white/[0.06] bg-surface md:max-h-none md:w-[360px] md:border-b-0 md:border-r">
          <div className="shrink-0 space-y-2.5 border-b border-white/[0.06] px-3 py-3">
            <div className="flex items-center justify-between gap-2">
              <div className="flex items-center gap-2">
                <div className="text-[11px] font-medium uppercase tracking-[0.04em] text-subtle">Events</div>
                <span className="rounded-md bg-white/5 px-1.5 py-px text-[10px] font-medium tabular-nums text-slate-300 ring-1 ring-white/10">
                  {clips.length}
                </span>
              </div>
              <div className="flex items-center gap-2.5">
                {filtersOn && (
                  <button
                    type="button"
                    className="text-[11px] text-slate-400 underline-offset-2 hover:text-white hover:underline"
                    onClick={clearFilters}
                  >
                    Reset
                  </button>
                )}
                <button
                  type="button"
                  className="text-[11px] font-semibold text-accent hover:text-white disabled:opacity-40"
                  disabled={clips.length === 0}
                  onClick={() => {
                    for (const c of clips) draft.add(c.id);
                  }}
                >
                  Add all
                </button>
              </div>
            </div>
            <div className="flex gap-1.5">
              <input
                id="clip-search"
                type="search"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search player, event, match…"
                className="input min-w-0 flex-1 py-1.5 text-xs"
              />
              <div className="w-[104px] shrink-0">
                <Select compact label="Sort" value={sort} onChange={(v) => setSort(v as SortKey)} options={[
                  { id: 'newest', label: 'Newest' },
                  { id: 'rating', label: 'Rating' },
                  { id: 'minute', label: 'Minute' },
                ]} />
              </div>
            </div>
            <div className="grid grid-cols-3 gap-1.5">
              <Select compact label="Match" value={matchId} onChange={setMatchId} options={[
                { id: '', label: 'Match' },
                ...matchOptions,
              ]} />
              <Select compact label="Event" value={eventType} onChange={setEventType} options={[
                { id: '', label: 'Event' },
                ...eventTypes.map((t) => ({ id: t, label: eventLabel(t) })),
              ]} />
              <Select compact label="Player" value={personId} onChange={setPersonId} options={[
                { id: '', label: 'Player' },
                ...persons,
              ]} />
              <Select compact label="Team" value={teamId} onChange={setTeamId} options={[
                { id: '', label: 'Team' },
                ...teams,
              ]} />
              <Select
                compact
                label="Rating"
                value={String(minRating)}
                onChange={(v) => setMinRating(v === '' ? '' : Number(v))}
                options={[
                  { id: '', label: 'Rating' },
                  { id: '5', label: '★★★★★' },
                  { id: '4', label: '★★★★' },
                  { id: '3', label: '★★★' },
                ]}
              />
              <input
                type="text"
                value={tag}
                onChange={(e) => setTag(e.target.value)}
                placeholder="Tag"
                aria-label="Tag"
                className="input py-1.5 text-xs"
              />
            </div>
          </div>

          <div className="min-h-0 flex-1 space-y-1 overflow-auto px-2 py-2">
            {clipsQuery.isLoading && clips.length === 0 && (
              <div className="px-4 py-16 text-center text-sm text-subtle">Loading events…</div>
            )}
            {clips.map((c) => (
              <EventRow
                key={c.id}
                clip={c}
                matchName={matchLabel(c.match_id)}
                stagedIndex={draft.clipIds.indexOf(c.id)}
                active={selectedId === c.id}
                canDrag={draft.clipIds.length > 0 && !clipIsUpdating(c)}
                onSelect={() => selectClip(c.id)}
                onToggleStage={() => draft.toggle(c.id)}
              />
            ))}
            {clips.length === 0 && !clipsQuery.isLoading && (
              <div className="px-4 py-16 text-center text-sm text-subtle">
                {filtersOn
                  ? 'No clips for these filters.'
                  : 'No published clips yet.'}
              </div>
            )}
          </div>
        </aside>

        <section className="flex min-h-0 min-w-0 flex-1 flex-col bg-base">
          {!selectedId ? (
            <div className="grid flex-1 place-items-center px-6 text-center text-sm text-subtle">
              Select an event to play.
            </div>
          ) : selectedGone || (selectedQuery.isError && !selected) ? (
            <div className="grid flex-1 place-items-center px-6 text-center text-sm text-subtle">
              This event is no longer available. Opening the next clip…
            </div>
          ) : selected ? (
            <>
              <div className="relative min-h-0 flex-1 bg-black">
                <div className="absolute inset-0">
                  {selectedBlocked ? (
                    <ClipUpdatingOverlay
                      poster={selected.preview_thumb_url}
                      stale={
                        !clipIsUpdating(selected) && !clipIsUpdating(selectedFromList)
                        && !!(selected?.preview_stale || selectedFromList?.preview_stale)
                      }
                      error={selected?.preview_error || selectedFromList?.preview_error}
                    />
                  ) : (
                    <ClipPlayer
                      key={`${selected.id}:${selected.preview_url ?? ''}:${selected.preview_generated_at ?? ''}`}
                      renderedMp4Url={selected.preview_url || undefined}
                      hlsUrl={undefined}
                      startMs={Number(selected.video_start_ms) || 0}
                      endMs={Number(selected.video_end_ms) || 0}
                      poster={selected.preview_thumb_url ?? undefined}
                      autoPlay
                      onEnded={onPreviewEnded}
                    />
                  )}
                </div>
                {(clipIsUpdating(selected) || clipIsUpdating(selectedFromList)) && !selectedBlocked && (
                  <span className="absolute right-3 top-3 z-10 flex items-center gap-1.5 rounded-md bg-black/70 px-2 py-1 text-[10px] font-semibold uppercase tracking-wide text-white ring-1 ring-white/15">
                    <span className="h-3 w-3 animate-spin rounded-full border-2 border-white/20 border-t-accent" />
                    Updating
                  </span>
                )}
                {reelIndex != null && (
                  <span className="absolute left-3 top-3 z-10 rounded-md bg-accent px-2 py-0.5 text-[10px] font-semibold text-[#0b1220]">
                    Reel {reelIndex + 1}/{draft.clipIds.length}
                  </span>
                )}
              </div>
              <div className="shrink-0 border-t border-white/[0.06] bg-surface px-4 py-3">
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="text-[15px] font-semibold text-white">
                      {eventLabel(selected.event_type)}
                      {selected.rating ? <span className="ml-2 text-sm font-medium text-amber-200">★{selected.rating}</span> : null}
                    </div>
                    <div className="text-[13px] text-slate-300">
                      {selected.person_name ?? selected.team_name ?? '-'}
                      {eventGameClock(selected) ? ` · ${eventGameClock(selected)}` : ''}
                      {` · ${formatClipClock(selected.duration_ms)}`}
                    </div>
                    {selected.chain ? (
                      <LinkedEventAccordion
                        key={`${selected.id}-detail`}
                        chain={selected.chain}
                        className="mt-2 max-w-md"
                      />
                    ) : null}
                    <div className="mt-0.5 text-[11px] text-subtle">
                      {matchLabel(selected.match_id) || `${selected.competition_code ?? ''} ${selected.season_name ?? ''}`}
                      {selected.period ? ` · ${selected.period}` : ''}
                    </div>
                    {selected.tags?.length ? (
                      <div className="mt-2 flex flex-wrap gap-1">
                        {selected.tags.map((t) => (
                          <span key={t} className="rounded-full bg-white/5 px-2 py-0.5 text-[10px] text-slate-300 ring-1 ring-white/10">
                            {t}
                          </span>
                        ))}
                      </div>
                    ) : null}
                  </div>
                  <div className="flex shrink-0 flex-wrap items-center gap-2">
                    <button
                      type="button"
                      className="btn-secondary px-2 py-1 text-xs"
                      disabled={selectedPos <= 0}
                      onClick={() => selectedPos > 0 && selectClip(clips[selectedPos - 1].id)}
                    >
                      ← Prev
                    </button>
                    <span className="text-[11px] tabular-nums text-subtle">
                      {selectedPos >= 0 ? `${selectedPos + 1} / ${clips.length}` : '-'}
                    </span>
                    <button
                      type="button"
                      className="btn-secondary px-2 py-1 text-xs"
                      disabled={selectedPos < 0 || selectedPos >= clips.length - 1}
                      onClick={() => selectedPos >= 0 && selectedPos < clips.length - 1 && selectClip(clips[selectedPos + 1].id)}
                    >
                      Next →
                    </button>
                    <button
                      type="button"
                      onClick={() => draft.toggle(selected.id)}
                      className={staged ? 'btn bg-rose-500/15 text-rose-100 ring-1 ring-rose-400/40 hover:bg-rose-500/25' : 'btn-primary'}
                    >
                      {staged ? 'Remove from timeline' : 'Add to timeline'}
                    </button>
                    {reelIndex != null && (
                      <button type="button" onClick={stopReel} className="btn-secondary">
                        Stop
                      </button>
                    )}
                  </div>
                </div>
              </div>
            </>
          ) : (
            <div className="grid flex-1 place-items-center text-sm text-subtle">Loading…</div>
          )}
        </section>
      </div>

      {draft.clipIds.length > 0 && (
        <TimelineBar
          selectedId={selectedId}
          reelIndex={reelIndex}
          onSelect={selectClip}
          onPlayReel={() => playReelFrom(0)}
          onStopReel={stopReel}
          onCreate={() => setComposeOpen(true)}
        />
      )}
      {composeOpen && (
        <CreateVideoDialog matchLabel={matchLabel} onClose={() => setComposeOpen(false)} />
      )}
    </div>
  );
}

function TimelineBar({
  selectedId, reelIndex, onSelect, onPlayReel, onStopReel, onCreate,
}: {
  selectedId: string | null;
  reelIndex: number | null;
  onSelect: (id: string) => void;
  onPlayReel: () => void;
  onStopReel: () => void;
  onCreate: () => void;
}) {
  const draft = useHighlightDraft();
  const { clips } = useStagedClips();
  const totalMs = clips.reduce((s, c) => s + (c ? c.duration_ms : 0), 0);
  const [dragFrom, setDragFrom] = useState<number | null>(null);
  const [dropAt, setDropAt] = useState<number | 'end' | null>(null);

  const maxMs = Math.max(1, ...clips.map((c) => c?.duration_ms ?? 0));

  return (
    <div className="shrink-0 border-t border-white/[0.06] bg-surface px-4 py-3">
      <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
        <div className="flex min-w-0 flex-wrap items-center gap-2">
          <span className="text-[11px] font-medium uppercase tracking-[0.04em] text-subtle">Reel</span>
          <span className="text-[12px] text-slate-400">
            <span className="font-medium text-slate-200">{draft.clipIds.length}</span>
            {draft.clipIds.length === 1 ? ' clip' : ' clips'}
            <span className="mx-1.5 text-white/20">·</span>
            <span className="font-mono text-slate-200">{formatClipClock(totalMs)}</span>
          </span>
          <span className="hidden text-[11px] text-slate-600 sm:inline">Drag events here</span>
        </div>
        <div className="flex shrink-0 items-center gap-2">
          {reelIndex != null ? (
            <button type="button" onClick={onStopReel} className="btn-secondary h-8 px-3 text-xs">
              Stop
            </button>
          ) : (
            <button type="button" onClick={onPlayReel} className="btn-secondary h-8 px-3 text-xs">
              Play
            </button>
          )}
          <button type="button" onClick={() => draft.clear()} className="h-8 px-2 text-xs text-slate-500 hover:text-slate-200">
            Clear
          </button>
          <button
            type="button"
            onClick={onCreate}
            disabled={draft.clipIds.length === 0}
            className="btn-primary h-8 px-3.5 text-xs"
          >
            Create video
          </button>
        </div>
      </div>

      <ol
        className="relative flex items-stretch gap-3 overflow-x-auto pb-0.5"
        onDragOver={(e) => {
          e.preventDefault();
          e.dataTransfer.dropEffect = dragFrom != null ? 'move' : 'copy';
          setDropAt('end');
        }}
        onDrop={(e) => {
          e.preventDefault();
          const incoming = readClipDrag(e);
          if (incoming) draft.insertAt(incoming, draft.clipIds.length);
          else if (dragFrom != null) draft.move(dragFrom, draft.clipIds.length - 1);
          setDragFrom(null);
          setDropAt(null);
        }}
      >
        {draft.clipIds.map((id, i) => {
          const clip = clips[i];
          const active = selectedId === id;
          const playing = reelIndex === i;
          const barPct = clip ? Math.max(14, Math.round((clip.duration_ms / maxMs) * 100)) : 14;
          return (
            <li
              key={id}
              draggable
              onDragStart={(e) => {
                setDragFrom(i);
                e.dataTransfer.setData(CLIP_DND, id);
                e.dataTransfer.setData('text/plain', id);
                e.dataTransfer.effectAllowed = 'copyMove';
              }}
              onDragOver={(e) => {
                e.preventDefault();
                e.stopPropagation();
                e.dataTransfer.dropEffect = dragFrom != null ? 'move' : 'copy';
                setDropAt(i);
              }}
              onDragEnd={() => { setDragFrom(null); setDropAt(null); }}
              onDrop={(e) => {
                e.preventDefault();
                e.stopPropagation();
                const incoming = readClipDrag(e);
                if (incoming) draft.insertAt(incoming, i);
                else if (dragFrom != null) draft.move(dragFrom, i);
                setDragFrom(null);
                setDropAt(null);
              }}
              className={`group relative flex w-[168px] shrink-0 flex-col overflow-hidden rounded-xl bg-elevated ring-1 ring-white/[0.06] transition ${
                playing
                  ? 'ring-white/20'
                  : active
                    ? 'bg-white/[0.07]'
                    : dropAt === i
                      ? 'bg-accent/10'
                      : 'hover:bg-white/[0.04]'
              }`}
            >
              {dropAt === i && (
                <span className="absolute -left-2 top-2 bottom-2 w-1 rounded-full bg-accent" />
              )}
              {(active || playing) && (
                <span className={`absolute inset-x-0 top-0 h-0.5 ${playing ? 'bg-accent' : 'bg-accent/70'}`} />
              )}
              <button type="button" onClick={() => onSelect(id)} className="relative block h-[86px] w-full overflow-hidden text-left">
                <ClipThumb clip={clip} className="h-full w-full" />
                <span className={`absolute left-2 top-2 grid h-5 min-w-5 place-items-center rounded-md px-1 text-[10px] font-semibold ${
                  active || playing ? 'bg-accent text-[#0b1220]' : 'bg-black/75 text-white'
                }`}>
                  {i + 1}
                </span>
                {playing && (
                  <span className="absolute right-2 top-2 rounded-md bg-accent px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-[0.04em] text-[#0b1220]">
                    Playing
                  </span>
                )}
                <span className="absolute inset-x-0 bottom-0 h-0.5 bg-black/35">
                  <span className="block h-full bg-accent/70" style={{ width: `${barPct}%` }} />
                </span>
              </button>
              <button
                type="button"
                onClick={() => onSelect(id)}
                className="flex w-full items-start justify-between gap-2 px-2.5 pb-1 pt-2 text-left"
              >
                <div className="min-w-0">
                  <div className="truncate text-[13px] font-semibold leading-tight text-white">
                    {clip ? eventLabel(clip.event_type) : '…'}
                  </div>
                  {(clip?.person_name || clip?.team_name) && (
                    <div className="mt-0.5 truncate text-[11px] leading-tight text-slate-500">
                      {clip.person_name || clip.team_name}
                    </div>
                  )}
                </div>
                <span className="shrink-0 pt-0.5 font-mono text-[10px] text-slate-400">
                  {clip ? formatClipClock(clip.duration_ms) : '-'}
                </span>
              </button>
              <div className="mt-auto flex items-center justify-between px-1.5 pb-1.5 pt-0.5 opacity-50 transition group-hover:opacity-100">
                <div className="flex gap-0.5">
                  <button
                    type="button"
                    disabled={i === 0}
                    onClick={(e) => { e.stopPropagation(); draft.move(i, i - 1); }}
                    className="grid h-7 w-7 place-items-center rounded-md text-slate-400 hover:bg-white/10 hover:text-white disabled:opacity-25"
                    title="Move left"
                  >
                    ←
                  </button>
                  <button
                    type="button"
                    disabled={i === draft.clipIds.length - 1}
                    onClick={(e) => { e.stopPropagation(); draft.move(i, i + 1); }}
                    className="grid h-7 w-7 place-items-center rounded-md text-slate-400 hover:bg-white/10 hover:text-white disabled:opacity-25"
                    title="Move right"
                  >
                    →
                  </button>
                </div>
                <button
                  type="button"
                  onClick={(e) => { e.stopPropagation(); draft.remove(id); }}
                  className="grid h-7 w-7 place-items-center rounded-md text-slate-500 hover:bg-rose-500/15 hover:text-rose-200"
                  title="Remove"
                >
                  ×
                </button>
              </div>
            </li>
          );
        })}
        <li
          className={`flex min-h-[150px] min-w-[160px] flex-1 flex-col items-center justify-center rounded-xl border border-dashed text-center transition ${
            dropAt === 'end'
              ? 'border-accent bg-accent/10 text-accent'
              : 'border-white/10 text-slate-600 hover:border-white/20 hover:text-slate-400'
          }`}
          onDragOver={(e) => {
            e.preventDefault();
            e.stopPropagation();
            e.dataTransfer.dropEffect = dragFrom != null ? 'move' : 'copy';
            setDropAt('end');
          }}
          onDrop={(e) => {
            e.preventDefault();
            e.stopPropagation();
            const incoming = readClipDrag(e);
            if (incoming) draft.insertAt(incoming, draft.clipIds.length);
            else if (dragFrom != null) draft.move(dragFrom, draft.clipIds.length - 1);
            setDragFrom(null);
            setDropAt(null);
          }}
        >
          <span className="text-[18px] leading-none">+</span>
          <span className="mt-1 px-3 text-[11px] leading-snug">Drop to add</span>
        </li>
      </ol>
    </div>
  );
}

function CreateVideoDialog({
  matchLabel, onClose,
}: {
  matchLabel: (id: string) => string;
  onClose: () => void;
}) {
  const draft = useHighlightDraft();
  const qc = useQueryClient();
  const nav = useNavigate();
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [transition, setTransition] = useState<ClipTransition>('fade');
  const { clips } = useStagedClips();
  const totalMs = clips.reduce((s, c) => s + (c ? c.duration_ms : 0), 0);

  const submit = useMutation({
    mutationFn: () => createHighlight({
      name: name.trim() || `Video ${new Date().toLocaleString()}`,
      description: description.trim() || null,
      clip_ids: draft.clipIds,
      watermark: false,
      transition,
    }),
    onSuccess: (res) => {
      const poster = clips.find((c) => c?.preview_thumb_url)?.preview_thumb_url ?? null;
      qc.setQueryData(['highlights'], (old: Highlight[] | undefined) => [{
        id: res.id,
        name: name.trim() || `Video ${new Date().toLocaleString()}`,
        description: description.trim() || null,
        clip_ids: [...draft.clipIds],
        watermark: false,
        total_duration_ms: res.total_duration_ms,
        render_status: 'queued',
        render_error: null,
        output_s3_key: null,
        output_size_bytes: null,
        output_ready_at: null,
        folder_id: null,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
        poster_url: res.poster_url || poster,
      }, ...(old ?? [])]);
      draft.clear();
      qc.invalidateQueries({ queryKey: ['highlights'] });
      onClose();
      nav('/videos');
    },
  });

  return (
    <div className="fixed inset-0 z-40 grid place-items-center bg-black/60 p-4 backdrop-blur-sm">
      <div className="card w-full max-w-lg p-5">
          <form
            className="space-y-3"
            onSubmit={(e) => { e.preventDefault(); submit.mutate(); }}
          >
            <div>
              <div className="text-[15px] font-semibold text-white">Create video</div>
              <p className="mt-1 text-[13px] text-subtle">
                {draft.clipIds.length} clips · {formatClipClock(totalMs)} - order from the timeline.
                {draft.clipIds.length > 1
                  ? ` ${CLIP_TRANSITIONS.find((t) => t.value === transition)?.label ?? 'Fade'} between each clip.`
                  : ''}
              </p>
            </div>
            <ol className="flex gap-1 overflow-x-auto pb-1">
              {draft.clipIds.map((id, i) => {
                const clip = clips[i];
                return (
                  <li key={id} className="w-20 shrink-0 overflow-hidden rounded-lg bg-black/40 ring-1 ring-white/10">
                    <div className="relative aspect-video">
                      <ClipThumb clip={clip} compact className="h-full w-full" />
                      <span className="absolute left-1 top-1 rounded bg-black/70 px-1 text-[9px] font-bold text-white">{i + 1}</span>
                    </div>
                    <div className="truncate px-1 py-0.5 text-[9px] text-slate-300">
                      {clip ? eventLabel(clip.event_type) : '…'}
                    </div>
                  </li>
                );
              })}
            </ol>
            <label className="block text-xs text-subtle">
              Name
              <input
                className="input mt-1"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder={clips[0] ? `Highlight - ${matchLabel(clips[0].match_id) || eventLabel(clips[0].event_type)}` : 'Top 5 goals'}
                autoFocus
              />
            </label>
            <label className="block text-xs text-subtle">
              Description (optional)
              <textarea
                className="textarea mt-1"
                rows={2}
                value={description}
                onChange={(e) => setDescription(e.target.value)}
              />
            </label>
            {draft.clipIds.length > 1 && (
              <label className="block text-xs text-subtle">
                Transition between clips
                <select
                  className="input mt-1"
                  value={transition}
                  onChange={(e) => setTransition(e.target.value as ClipTransition)}
                >
                  {CLIP_TRANSITIONS.map((t) => (
                    <option key={t.value} value={t.value}>{t.label}</option>
                  ))}
                </select>
                <span className="mt-1 block text-[10px] text-slate-500">
                  {CLIP_TRANSITIONS.find((t) => t.value === transition)?.hint}
                </span>
              </label>
            )}
            {submit.isError && (
              <div className="alert-bad text-[13px]">
                {(submit.error as Error).message}
              </div>
            )}
            <div className="flex justify-end gap-2 pt-1">
              <button type="button" onClick={onClose} className="btn-secondary">Cancel</button>
              <button type="submit" disabled={submit.isPending || draft.clipIds.length === 0} className="btn-primary">
                {submit.isPending ? 'Creating…' : 'Create video'}
              </button>
            </div>
          </form>
      </div>
    </div>
  );
}

function EventRow({
  clip, matchName, active, stagedIndex, canDrag, onSelect, onToggleStage,
}: {
  clip: Clip;
  matchName: string;
  active: boolean;
  stagedIndex: number;
  canDrag: boolean;
  onSelect: () => void;
  onToggleStage: () => void;
}) {
  const [chainOpen, setChainOpen] = useState(false);
  const staged = stagedIndex >= 0;
  const updating = clipIsUpdating(clip);
  const details = [
    eventGameClock(clip) ?? clip.period,
    matchName || clip.competition_code,
  ].filter(Boolean).join(' · ');

  return (
    <article
      data-clip-row={clip.id}
      draggable={canDrag}
      onDragStart={(e) => {
        if (!canDrag) {
          e.preventDefault();
          return;
        }
        e.dataTransfer.setData(CLIP_DND, clip.id);
        e.dataTransfer.setData('text/plain', clip.id);
        e.dataTransfer.effectAllowed = 'copy';
      }}
      title={canDrag ? 'Drag onto the timeline to add' : undefined}
      className={`rounded-xl px-2 py-2 transition ${
        canDrag ? 'cursor-grab active:cursor-grabbing' : ''
      } ${
        active
          ? 'bg-white/[0.07] ring-1 ring-white/15'
          : 'bg-elevated/60 ring-1 ring-white/[0.05] hover:bg-white/[0.05] hover:ring-white/10'
      }`}
    >
      <div className="flex items-center gap-2.5">
        <button
          type="button"
          onClick={onSelect}
          className="flex min-w-0 flex-1 items-center gap-2.5 text-left"
        >
          <div className="relative shrink-0">
            <ClipThumb
              clip={clip}
              className="h-[48px] w-[78px] rounded-lg ring-1 ring-white/10"
            />
            <span className="absolute bottom-0.5 right-0.5 rounded bg-black/75 px-1 text-[9px] font-semibold tabular-nums text-white">
              {formatClipClock(clip.duration_ms)}
            </span>
          </div>
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-1.5">
              <div className="truncate text-[13px] font-semibold leading-tight text-white">
                {eventLabel(clip.event_type)}
              </div>
              {clip.rating ? (
                <span className="shrink-0 text-[10px] font-bold text-amber-200">★{clip.rating}</span>
              ) : null}
              {staged ? (
                <span className="shrink-0 rounded-md bg-accent px-1.5 text-[9px] font-semibold leading-4 text-[#0b1220]">
                  #{stagedIndex + 1}
                </span>
              ) : null}
              <span className={`shrink-0 rounded-full px-1.5 text-[9px] font-bold leading-4 ${
                clipHasMp4(clip)
                  ? 'bg-emerald-500/20 text-emerald-100'
                  : 'bg-white/10 text-slate-400'
              }`}>
                {clipHasMp4(clip) ? 'Ready' : 'No MP4'}
              </span>
            </div>
            <div className="truncate text-[12px] leading-snug text-slate-300">
              {clip.person_name ?? clip.team_name ?? '-'}
            </div>
            <div className="truncate text-[10px] leading-tight text-slate-500">
              {details}
            </div>
          </div>
        </button>
        <button
          type="button"
          disabled={updating}
          onMouseDown={(e) => e.stopPropagation()}
          onClick={(e) => { e.stopPropagation(); onToggleStage(); }}
          className={`shrink-0 self-center rounded-lg px-2 py-1 text-[11px] font-semibold ring-1 ${
            updating
              ? 'cursor-wait opacity-40 ring-white/10'
              : staged
                ? 'bg-white/10 text-white ring-white/15'
                : 'bg-white/5 text-slate-300 ring-white/10 hover:bg-white/10'
          }`}
          title={staged ? 'Remove from timeline' : 'Add to timeline'}
        >
          {staged ? '−' : '+'}
        </button>
      </div>
      {clip.chain ? (
        <LinkedEventAccordion
          chain={clip.chain}
          open={chainOpen}
          onToggle={() => setChainOpen((v) => !v)}
        />
      ) : null}
    </article>
  );
}

function clipIsUpdating(clip?: { preview_generating?: boolean } | null): boolean {
  return !!clip?.preview_generating;
}

function mergeClipIntoQueryData(old: unknown, clip: Clip): unknown {
  if (Array.isArray(old)) {
    let hit = false;
    const next = old.map((c: Clip) => {
      if (c?.id !== clip.id) return c;
      hit = true;
      return { ...c, ...clip, chain: clip.chain ?? c.chain };
    });
    return hit ? next : old;
  }
  if (old && typeof old === 'object' && 'clips' in old) {
    const staged = old as { clips: Array<Clip | null> };
    if (!Array.isArray(staged.clips)) return old;
    return {
      ...staged,
      clips: staged.clips.map((c) => (c && c.id === clip.id ? { ...c, ...clip, chain: clip.chain ?? c.chain } : c)),
    };
  }
  return old;
}

function clipHasMp4(clip?: Clip | null): boolean {
  return !!clip?.preview_url && !clipPreviewBlocked(clip);
}

function clipIsPlayable(clip?: Clip | null): boolean {
  if (!clip?.id || !clip.match_id) return false;
  return clipHasMp4(clip);
}

function nextPlayableId(clips: Clip[], fromId: string | null): string | null {
  const playable = clips.filter(clipIsPlayable);
  if (playable.length === 0) return null;
  if (!fromId) return playable[0].id;
  const idx = clips.findIndex((c) => c.id === fromId);
  const start = idx >= 0 ? idx : -1;
  for (let i = 1; i <= clips.length; i++) {
    const c = clips[(start + i) % clips.length];
    if (c && clipIsPlayable(c)) return c.id;
  }
  return playable[0].id;
}

function dropClipFromQueryData(old: unknown, clipId: string): unknown {
  if (Array.isArray(old)) return old.filter((c: Clip) => c?.id !== clipId);
  if (old && typeof old === 'object' && 'clips' in old) {
    const staged = old as { clips: Array<Clip | null> };
    if (!Array.isArray(staged.clips)) return old;
    return { ...staged, clips: staged.clips.filter((c) => !c || c.id !== clipId) };
  }
  return old;
}

function clipPreviewBlocked(clip?: {
  preview_generating?: boolean;
  preview_stale?: boolean;
  preview_url?: string | null;
  updated_at?: string;
  preview_generated_at?: string | null;
} | null) {
  if (!clip) return false;
  if (clipIsUpdating(clip)) return !clip.preview_url;
  return !!clip.preview_stale && !clip.preview_url;
}

function ClipThumb({
  clip, className, compact,
}: {
  clip?: Clip | null;
  className?: string;
  compact?: boolean;
}) {
  const updating = clipIsUpdating(clip) && !clip?.preview_url;
  const stale = !clipIsUpdating(clip) && !!clip?.preview_stale && !clip?.preview_url;
  return (
    <div className={`relative overflow-hidden bg-black ${className ?? ''}`}>
      {clip?.preview_thumb_url ? (
        <img
          src={clip.preview_thumb_url}
          alt=""
          className={`h-full w-full object-cover ${updating || stale ? 'opacity-40' : ''}`}
        />
      ) : (
        <div className="h-full w-full" />
      )}
      {updating && (
        <div className="absolute inset-0 flex flex-col items-center justify-center gap-0.5 bg-black/55">
          <span className={`${compact ? 'h-3 w-3' : 'h-4 w-4'} animate-spin rounded-full border-2 border-white/25 border-t-accent`} />
          <span className={`${compact ? 'text-[7px]' : 'text-[8px]'} font-semibold uppercase tracking-wide text-white`}>
            Updating
          </span>
        </div>
      )}
      {stale && (
        <div className="absolute inset-0 grid place-items-center bg-black/50">
          <span className="text-[8px] font-semibold uppercase text-amber-100">Recut</span>
        </div>
      )}
    </div>
  );
}

function ClipUpdatingOverlay({
  poster, stale, error,
}: {
  poster?: string | null;
  stale?: boolean;
  error?: string | null;
}) {
  return (
    <div className="relative h-full w-full bg-black">
      {poster ? (
        <img src={poster} alt="" className="h-full w-full object-cover opacity-35" />
      ) : null}
      <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 bg-black/45 px-4 text-center">
        {stale ? (
          <>
            <span className="text-[11px] font-semibold uppercase tracking-wide text-amber-100">New video not built</span>
            <span className="max-w-[240px] text-[10px] leading-snug text-slate-300">
              {error?.includes('outside recorded range')
                ? 'IN/OUT is outside the current recording. Recut on the new tape, then Save.'
                : 'The last Save did not produce a new MP4. Recut IN/OUT on the current recording and Save again.'}
            </span>
          </>
        ) : (
          <>
            <span className="h-8 w-8 animate-spin rounded-full border-2 border-white/20 border-t-accent" />
            <span className="text-[11px] font-semibold uppercase tracking-wide text-white">Updating clip…</span>
          </>
        )}
      </div>
    </div>
  );
}

function Select({
  label, value, onChange, options, compact,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  options: Array<{ id: string; label: string }>;
  compact?: boolean;
}) {
  return (
    <label className={compact ? 'min-w-0' : 'text-xs text-subtle'}>
      {compact ? <span className="sr-only">{label}</span> : label}
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        title={label}
        className={compact ? 'select py-1.5 text-xs' : 'select mt-1'}
      >
        {options.map((o) => (
          <option key={o.id} value={o.id}>{o.label}</option>
        ))}
      </select>
    </label>
  );
}

function LinkedEventAccordion({
  chain,
  open: openProp,
  onToggle,
  className = '',
}: {
  chain: ClipChain;
  open?: boolean;
  onToggle?: () => void;
  className?: string;
}) {
  const [internalOpen, setInternalOpen] = useState(false);
  const open = openProp ?? internalOpen;
  const toggle = onToggle ?? (() => setInternalOpen((v) => !v));
  const who = chain.person_name ?? chain.team_name ?? '-';
  const label = eventLabel(chain.event_type);

  return (
    <div className={`mt-2 pl-2 ${className}`}>
      <div className="relative border-l border-white/15 pl-3">
        <span className="absolute -left-px top-2 h-2 w-2 -translate-x-1/2 rounded-full bg-accent ring-2 ring-surface" />
        <button
          type="button"
          onMouseDown={(e) => e.stopPropagation()}
          onClick={(e) => { e.stopPropagation(); toggle(); }}
          aria-expanded={open}
          className="flex w-full items-center gap-2 rounded-lg px-1.5 py-1 text-left hover:bg-white/5"
        >
          <span className="min-w-0 flex-1 truncate text-[11px] leading-snug text-slate-300">
            <span className="mr-1.5 text-[9px] font-medium uppercase tracking-[0.04em] text-subtle">Linked</span>
            {label}
            <span className="text-subtle"> · {who}</span>
          </span>
          {chain.rating ? (
            <span className="shrink-0 text-[10px] font-bold text-amber-200">★{chain.rating}</span>
          ) : null}
          <svg
            width="12"
            height="12"
            viewBox="0 0 16 16"
            fill="none"
            aria-hidden
            className={`shrink-0 text-subtle transition-transform ${open ? 'rotate-180' : ''}`}
          >
            <path d="M4 6l4 4 4-4" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </button>
        {open ? (
          <div className="mt-1 mb-0.5 rounded-lg bg-white/[0.04] px-3 py-2.5 ring-1 ring-white/10">
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0">
                <div className="text-[13px] font-semibold text-white">{label}</div>
                <div className="mt-0.5 truncate text-[12px] text-slate-200">{who}</div>
                {chain.team_name && chain.person_name ? (
                  <div className="truncate text-[11px] text-slate-400">{chain.team_name}</div>
                ) : null}
              </div>
              {chain.rating ? (
                <span className="shrink-0 text-[11px] font-bold text-amber-200">★{chain.rating}</span>
              ) : null}
            </div>
            {eventGameClock(chain) ? (
              <div className="mt-1.5 font-mono text-[11px] tabular-nums text-slate-400">{eventGameClock(chain)}</div>
            ) : null}
          </div>
        ) : null}
      </div>
    </div>
  );
}

function uniqueByID<T>(rows: T[], pick: (r: T) => { id: string; label: string } | null): Array<{ id: string; label: string }> {
  const seen = new Map<string, string>();
  for (const r of rows) {
    const kv = pick(r);
    if (kv && !seen.has(kv.id)) seen.set(kv.id, kv.label);
  }
  return Array.from(seen.entries()).map(([id, label]) => ({ id, label }));
}

function UpsellCard() {
  return (
    <div className="grid h-full min-h-0 flex-1 place-items-center px-6 py-8">
      <div className="card w-full max-w-md p-8 text-center">
        <p className="text-[11px] font-medium uppercase tracking-[0.04em] text-subtle">
          Clips is not on this account yet.
        </p>
        <h2 className="mt-2 text-[1.25rem] font-semibold text-white">Enable video clips</h2>
        <p className="mt-2 text-[13px] leading-relaxed text-subtle">
          Every PBP event gets a video window. Filter, preview, then build a reel.
        </p>
        <a
          href="mailto:sales@origo.live?subject=Enable%20Clips%20for%20our%20account"
          className="btn-primary mt-5"
        >
          Enable Clips
        </a>
      </div>
    </div>
  );
}
